import React from 'react';

/* ─── Skeleton placeholders ───────────────────────────────────────────────────
   La classe .skeleton-shimmer est fournie par tailwind.config.shared.js (plugin
   « @gs/ui — composants partagés », couleurs sourcées theme()). Ne PAS la
   redéfinir localement dans le index.css d'une app. */

export function SkeletonLine({ className = '' }: { className?: string }) {
  return <div className={`skeleton-shimmer rounded ${className}`} />;
}

export function SkeletonKpi() {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <SkeletonLine className="h-3 w-20" />
          <SkeletonLine className="h-7 w-28" />
        </div>
        <SkeletonLine className="h-9 w-9 rounded-lg" />
      </div>
    </div>
  );
}

export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-4 space-y-3">
      <SkeletonLine className="h-4 w-2/3" />
      {Array.from({ length: lines - 1 }).map((_, i) => (
        <SkeletonLine key={i} className="h-3 w-full" />
      ))}
    </div>
  );
}

function SkeletonTableRow({ cols }: { cols: number }) {
  return (
    <tr className="border-b border-slate-100">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <SkeletonLine className={`h-3 ${i === 0 ? 'w-32' : 'w-20'}`} />
        </td>
      ))}
    </tr>
  );
}

export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-4 py-3 text-left">
                <SkeletonLine className="h-3 w-16" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} cols={cols} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SkeletonMatrix({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <table className="min-w-full text-sm">
        <thead>
          <tr>
            <th className="bg-slate-100 px-3 py-2 border-b border-r border-slate-200">
              <SkeletonLine className="h-3 w-16" />
            </th>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="bg-slate-100 px-3 py-2 border-b border-slate-200">
                <SkeletonLine className="h-3 w-10 mx-auto" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r} className="border-b border-slate-100">
              <td className="px-3 py-2 border-r border-slate-200">
                <SkeletonLine className="h-3 w-24" />
              </td>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c} className="px-3 py-2 text-center">
                  <SkeletonLine className="h-3 w-8 mx-auto" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SkeletonChart() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <SkeletonLine className="h-4 w-40" />
        <SkeletonLine className="h-8 w-36 rounded-lg" />
      </div>
      <div className="h-72 bg-slate-50 rounded-lg" />
    </div>
  );
}
